/**
 * Resolve binary sex for matching — never invent a default for signed-in members.
 * Sources (in order): explicit value → guest id suffix → onboarding progress → draft.
 */
import { onboardingStore } from "../auth/onboardingStore";
import type { Gender } from "./types";

export function isBinaryGender(v: unknown): v is Gender {
  return v === "male" || v === "female";
}

/** Guest ids are `user_guest_{install}_{male|female}`. */
export function genderFromGuestUserId(userId: string): Gender | null {
  if (!userId.startsWith("user_guest_")) return null;
  if (userId.endsWith("_female")) return "female";
  if (userId.endsWith("_male")) return "male";
  return null;
}

/** Sex chosen on the Profile onboarding step (`un8.onboarding.progress.v1`). */
export function genderFromOnboarding(userId: string): Gender | null {
  const saved = onboardingStore.load(userId);
  const g = saved?.answers?.gender;
  return isBinaryGender(g) ? g : null;
}

/** Last pre-account Profile step (`un8.onboarding.draft.v1`). */
export function genderFromOnboardingDraft(): Gender | null {
  const draft = onboardingStore.loadDraft();
  const g = draft?.answers?.gender;
  return isBinaryGender(g) ? g : null;
}

/**
 * Demo-only: draft sex if the guest already completed Profile, else one silent
 * path (male → opposite-sex women). Matching UI never asks man/woman.
 */
export function silentDemoGender(): Gender {
  return genderFromOnboardingDraft() ?? "male";
}

/**
 * Resolve viewer sex for a match session.
 * Do not default to male for members — missing sex must send them to Profile.
 */
export function resolveViewerGender(input: {
  explicit?: unknown;
  userId?: string | null;
}): Gender | null {
  if (isBinaryGender(input.explicit)) return input.explicit;
  const id = input.userId;
  if (!id) return genderFromOnboardingDraft();
  const fromGuest = genderFromGuestUserId(id);
  if (fromGuest) return fromGuest;
  return genderFromOnboarding(id) ?? genderFromOnboardingDraft();
}
